/**
 * File Upload Zone Component
 * TASK-361: Document Management & Compliance System
 *
 * Drag-and-drop file upload zone for the public upload portal.
 * Supports multiple file selection and provides validation feedback.
 */

import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import {
  Upload,
  File,
  FileText,
  Image,
  Table,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { DocType, getDocTypeLabel } from '../../types/documentCompliance';
import { publicUploadService } from '../../services/publicUploadService';
import { formatFileSize } from '../../utils/formatters';

interface FileUploadZoneProps {
  allowedDocTypes: DocType[];
  onUpload: (file: File, docType: DocType) => Promise<void>;
  uploadsRemaining: number;
  disabled?: boolean;
}

interface SelectedFile {
  file: File;
  docType: DocType;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  allowedDocTypes,
  onUpload,
  uploadsRemaining,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [selectedDocType, setSelectedDocType] = useState<DocType>(allowedDocTypes[0] || 'general');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
      return <Table className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      processFiles(files);
    }
  };

  const processFiles = (files: File[]) => {
    const newFiles: SelectedFile[] = files.map((file) => {
      const validation = publicUploadService.validateFile(file);
      return {
        file,
        docType: selectedDocType,
        status: validation.valid ? 'pending' : 'error',
        progress: 0,
        error: validation.error,
      };
    });

    setSelectedFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (index: number) => {
    const fileEntry = selectedFiles[index];
    if (fileEntry.status !== 'pending') return;

    setSelectedFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, status: 'uploading' as const, progress: 0 } : f))
    );

    try {
      await onUpload(fileEntry.file, fileEntry.docType);
      setSelectedFiles((prev) =>
        prev.map((f, i) => (i === index ? { ...f, status: 'success' as const, progress: 100 } : f))
      );
    } catch (err) {
      setSelectedFiles((prev) =>
        prev.map((f, i) =>
          i === index
            ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : 'Upload failed' }
            : f
        )
      );
    }
  };

  const handleUploadAll = async () => {
    const pendingFiles = selectedFiles
      .map((f, i) => ({ ...f, index: i }))
      .filter((f) => f.status === 'pending');

    for (const file of pendingFiles) {
      await handleUpload(file.index);
    }
  };

  const pendingCount = selectedFiles.filter((f) => f.status === 'pending').length;
  const canUploadMore = uploadsRemaining > 0;

  return (
    <div className="space-y-4">
      {/* Document Type Selection */}
      {allowedDocTypes.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document Type
          </label>
          <div className="flex flex-wrap gap-2">
            {allowedDocTypes.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedDocType(type)}
                className={`
                  px-3 py-1.5 text-sm rounded-lg border transition-colors
                  ${selectedDocType === type
                    ? 'border-[#002B45] bg-[#002B45]/5 text-[#002B45]'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }
                `}
              >
                {getDocTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isDragging
              ? 'border-[#F7B500] bg-[#F7B500]/5'
              : 'border-gray-300 hover:border-[#002B45] hover:bg-gray-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.heic,.xlsx,.xls,.csv,.docx,.pptx"
          disabled={disabled}
        />

        <Upload className={`w-10 h-10 mx-auto mb-3 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />

        {disabled ? (
          <p className="text-gray-500">Upload limit reached</p>
        ) : (
          <>
            <p className="text-gray-700 font-medium mb-1">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-gray-500">
              PDF, Images, Word, Excel, or CSV (max 20MB each)
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {uploadsRemaining} upload{uploadsRemaining !== 1 ? 's' : ''} remaining
            </p>
          </>
        )}
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </h4>
            {pendingCount > 0 && canUploadMore && (
              <button
                onClick={handleUploadAll}
                className="px-4 py-1.5 text-sm bg-[#002B45] text-white rounded-lg hover:bg-[#003a5c] transition-colors"
              >
                Upload All ({pendingCount})
              </button>
            )}
          </div>

          <div className="space-y-2">
            {selectedFiles.map((fileEntry, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getFileIcon(fileEntry.file.type)}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileEntry.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileEntry.file.size)} • {getDocTypeLabel(fileEntry.docType)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Status */}
                  {fileEntry.status === 'pending' && (
                    <button
                      onClick={() => handleUpload(index)}
                      disabled={!canUploadMore}
                      className="px-3 py-1 text-xs bg-[#002B45] text-white rounded hover:bg-[#003a5c] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Upload
                    </button>
                  )}
                  {fileEntry.status === 'uploading' && (
                    <Loader2 className="w-5 h-5 text-[#002B45] animate-spin" />
                  )}
                  {fileEntry.status === 'success' && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  {fileEntry.status === 'error' && (
                    <div className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-xs">{fileEntry.error}</span>
                    </div>
                  )}

                  {/* Remove button */}
                  {fileEntry.status !== 'uploading' && (
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
